/**
 * projectionBreakdownStyles.ts – the stylesheet for a projection year's human-readable breakdown
 * (`ProjectionYearResult.breakdown`) as the projections modal renders it.
 *
 * Written as CSS rather than utility classes because the whole design is margins and pseudo-elements —
 * a coloured marker beside each paragraph, a 🎉 beside each birthday, a month's name stood on its end
 * down the left of its band — and the breakdown HTML itself must stay plain semantic markup that any
 * other surface can render (see BREAKDOWN_SERIES_TAGS). The engine emits one wrapper, the month band,
 * and no spacers or icons at all; everything below hangs off that class, the paragraph tags and the
 * element names alone.
 *
 * The marker colours are BUILT from the accordion's own chart series, so a paragraph's marker is
 * always exactly the colour of the bar it describes — rename or recolour a series and this follows.
 *
 * Two layouts, because the month's name has to go somewhere: below
 * {@link BREAKDOWN_RAIL_MIN_WIDTH} it is a grey bar across the top of its band, and above it the same
 * bar turned 90° into a rail down the left. Nothing else changes between them.
 */
import { BREAKDOWN_BIRTHDAY_TAG, BREAKDOWN_MONTH_CLASS, BREAKDOWN_SERIES_TAGS } from '../../../../shared/src/calculation/constants/breakdownSeriesTags.ts';
import type { ChartSeries } from '../../components/charts/chartTypes.ts';
import { CASHFLOW_INCOME_SERIES } from './cashflowChartData.ts';
import { DEBT_SERIES, NET_WORTH_SERIES } from './balanceSheetChartData.ts';

/** Root class the breakdown container carries; every rule below is scoped to it. */
export const PROJECTION_BREAKDOWN_CLASS = 'projection-breakdown';

/**
 * The viewport width at which a month's heading stops being a bar above its band and becomes a rail
 * down the left of it. Tailwind's `sm`, which is where the modal itself stops being a single column.
 */
export const BREAKDOWN_RAIL_MIN_WIDTH = '640px';

/**
 * `<chart>-<series id>` → the colour that chart draws the series in. The tag vocabulary is shared
 * with the engine; this is the half that knows what the colours are.
 */
export function breakdownSeriesColors(): Record<string, string> {
    const colors: Record<string, string> = {};
    const charts: [string, readonly ChartSeries[]][] = [
        ['cashflow', CASHFLOW_INCOME_SERIES],
        ['networth', NET_WORTH_SERIES],
        ['debt', DEBT_SERIES],
    ];
    charts.forEach(([chart, series]) => series.forEach((entry) => (colors[`${chart}-${entry.id}`] = entry.color)));
    return colors;
}

/**
 * A calendar, as a background image so it needs no element of its own. Single-quoted attributes and
 * an escaped `#` keep it legal inside `url("…")`; the stroke is passed in because the icon is drawn
 * white on the month band's grey heading.
 */
const calendarIcon = (stroke: string) =>
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${stroke}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2'/%3E%3Cpath d='M16 2v4M8 2v4M3 10h18'/%3E%3C/svg%3E")`;

/** Only tags the engine actually emits get a marker rule — anything else leaves the gutter empty. */
function markerRules(): string {
    const colors = breakdownSeriesColors();
    const tagged = BREAKDOWN_SERIES_TAGS.filter((tag) => colors[tag]);
    // One shared geometry rule, then one colour per tag. `:where()` keeps specificity flat, so the
    // colour declarations below win without needing to repeat the selector list.
    const geometry = `.${PROJECTION_BREAKDOWN_CLASS} p:where(${tagged.map((tag) => `.${tag}`).join(', ')})::before`;
    return [
        `${geometry} {
            content: '';
            position: absolute;
            left: calc((var(--breakdown-gutter) - var(--breakdown-marker)) / 2);
            /* Inset from both ends by half a line, so one line reads as a dot and several as a bar. */
            top: calc((var(--breakdown-line) - var(--breakdown-marker)) / 2);
            bottom: calc((var(--breakdown-line) - var(--breakdown-marker)) / 2);
            width: var(--breakdown-marker);
            border-radius: 9999px;
            background-color: var(--breakdown-marker-color);
        }`,
        ...tagged.map((tag) => `.${PROJECTION_BREAKDOWN_CLASS} p.${tag} { --breakdown-marker-color: ${colors[tag]}; }`),
    ].join('\n');
}

/**
 * The whole stylesheet, for a single `<style>` element. Values match the utility classes this
 * replaced (slate 300/600/800, text-sm, etc.) so only the gutter and its markers are new.
 */
export const PROJECTION_BREAKDOWN_CSS = `
.${PROJECTION_BREAKDOWN_CLASS} {
    /* The gutter every block indents past, and which the markers live in. */
    --breakdown-gutter: 1.5rem;
    /* How far a month band holds its text off its own right-hand edge. */
    --breakdown-inset: 0.75rem;
    --breakdown-marker: 8px;
    --breakdown-line: 1.6em;
    font-size: 0.875rem;
    line-height: var(--breakdown-line);
    color: #334155;
}
/* One band per month (the engine's own grouping), alternating white and off-white so a reader
   scrolling a year of fortnightly movements can see where one month ends and the next begins without
   stopping to read a date. */
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} {
    /* The rail the month's name stands up in — claimed by the wide layout below, nothing until then. */
    --breakdown-rail: 0px;
    position: relative;
    margin: 0.5rem 0;
    padding: 0 var(--breakdown-inset) 0.5rem var(--breakdown-rail);
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    /* Clips the heading — full-bleed across the band's top, or down its rail — to those corners. */
    overflow: hidden;
    background-color: #ffffff;
}
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS}:nth-child(even) { background-color: #f8fafc; }
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS}:first-child { margin-top: 0; }
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS}:last-child { margin-bottom: 0; }
/* The month's name, and the only place the calendar icon now appears: a narrow viewport gets a grey
   bar across the top of the band, in white, which is where this heading has always read from. */
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} > h2 {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    /* Escapes the band's right-hand inset so the bar runs edge to edge, corner to clipped corner. */
    margin: 0 calc(-1 * var(--breakdown-inset)) 0 0;
    padding: 0.25rem 0.75rem;
    background-color: #64748b;
    color: #ffffff;
    font-size: 0.75rem;
    line-height: 1.25rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} > h2::before {
    content: '';
    /* Never squeezed by the label beside it, and never turned on its side by the rail's writing mode. */
    flex: none;
    width: 0.875rem;
    height: 0.875rem;
    background: ${calendarIcon('%23ffffff')} no-repeat center / contain;
}
/* Day headings — the ordinal alone, because the band around them names the month and year. Their rule
   spans the band's full width, gutter included, while the text starts after it. */
.${PROJECTION_BREAKDOWN_CLASS} h1 {
    margin: 1.25rem calc(-1 * var(--breakdown-inset)) 0.5rem 0;
    padding: 0 0 0.25rem var(--breakdown-gutter);
    border-bottom: 1px solid #cbd5e1;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: #1e293b;
}
/* The band's own heading already opens it, so its first day sits closer than a mid-band one. */
.${PROJECTION_BREAKDOWN_CLASS} h2 + h1 { margin-top: 0.5rem; }
/* The ordinal's raised suffix (\`1<sup>st</sup>\`). Smaller than the browser default's \`smaller\`, and
   lifted by a fixed amount, so it reads as part of the number rather than as a footnote mark. */
.${PROJECTION_BREAKDOWN_CLASS} h1 sup {
    font-size: 0.65em;
    vertical-align: baseline;
    position: relative;
    top: -0.45em;
}
.${PROJECTION_BREAKDOWN_CLASS} p {
    position: relative;
    margin: 0.25rem 0;
    padding-left: var(--breakdown-gutter);
}
.${PROJECTION_BREAKDOWN_CLASS} table {
    margin: 0.5rem 0 0.5rem var(--breakdown-gutter);
    width: calc(100% - var(--breakdown-gutter));
    border-collapse: collapse;
    border: 1px solid #cbd5e1;
}
.${PROJECTION_BREAKDOWN_CLASS} th,
.${PROJECTION_BREAKDOWN_CLASS} td {
    border: 1px solid #cbd5e1;
    padding: 0.25rem 0.5rem;
}
.${PROJECTION_BREAKDOWN_CLASS} th {
    background-color: #f1f5f9;
    text-align: left;
    font-weight: 600;
    color: #475569;
}
.${PROJECTION_BREAKDOWN_CLASS} td {
    text-align: right;
    font-variant-numeric: tabular-nums;
}
.${PROJECTION_BREAKDOWN_CLASS} tr.total {
    border-top: 2px solid #94a3b8;
    font-weight: 700;
}
.${PROJECTION_BREAKDOWN_CLASS} tr.total > * { background-color: #e2e8f0; }
/* A birthday's gutter carries a glyph instead of a series marker — nothing on any chart moves, so
   there is no bar colour to echo. Centred in the gutter and locked to the first line's height, so a
   sentence that wraps keeps its emoji beside the line that names the person. */
.${PROJECTION_BREAKDOWN_CLASS} p.${BREAKDOWN_BIRTHDAY_TAG}::before {
    content: '🎉';
    position: absolute;
    left: 0;
    top: 0;
    width: var(--breakdown-gutter);
    line-height: var(--breakdown-line);
    /* As big as the gutter will take without crowding the sentence beside it. */
    font-size: 1rem;
    text-align: center;
}
${markerRules()}
/* Wide viewport: the month's grey bar is stood on its end and becomes a rail down the band's left
   edge, which buys back the line it spent across the top and leaves the label visible the whole way
   down a long month. Narrow stays the bar above — a rail costs horizontal room a phone hasn't got. */
@media (min-width: ${BREAKDOWN_RAIL_MIN_WIDTH}) {
    .${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} {
        --breakdown-rail: 1.75rem;
        /* A band with one sentence in it is shorter than its own label standing up, and would clip it.
           Tall enough for the icon, the gap and eight uppercase characters of \`MMM YYYY\`. */
        min-height: 7.5rem;
    }
    .${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} > h2 {
        position: absolute;
        /* The band's padding box, i.e. the rail its left padding reserved. */
        left: 0;
        top: 0;
        bottom: 0;
        width: var(--breakdown-rail);
        margin: 0;
        padding: 0.5rem 0;
        /* Stands the label up reading BOTTOM TO TOP, in two steps because no writing mode does that on
           its own that every browser has: \`vertical-rl\` turns it 90° reading downwards, then a half turn
           of the whole box reverses it. (\`sideways-lr\` would say this in one line, and is too new.) */
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        /* Along the rail (the inline axis, now vertical) and then across it. */
        justify-content: center;
        align-items: center;
        /* The half turn moves main-start to the bottom of the rail, so the icon — the first item — is
           laid out from there and lands back at the TOP once the box is turned. */
        flex-direction: row-reverse;
    }
    /* Turned back the right way up: the label is upside down by design, an icon never is. */
    .${PROJECTION_BREAKDOWN_CLASS} .${BREAKDOWN_MONTH_CLASS} > h2::before { transform: rotate(180deg); }
}
`;
